/**
 * +acolyte-specs (S6, docs/design/classes/specs.md "Sim features"): damage-to-healing transfer.
 *
 * An aura with `transfer` (the Lightbinder's Absolution passive) turns the holder's damage into
 * healing: every time the holder damages a hostile, `pct` of what landed (after mitigation,
 * absorbs included) heals each ally within `radius` that carries the holder's `markAuraId`. With
 * more than `shareAbove` marked allies the total is split evenly, so marking the whole party ramps
 * coverage, not raw throughput. The heal is pre-scaled (the damage already carried the holder's
 * multipliers), never crits, and is logged under the mark's id so meters show "Absolution".
 */
import type { DamageRequest, SimContext, Unit } from '../contracts';
import { heal } from './damage';
import { unitOf } from './util';

/** Heal the holder's marked allies for a share of `taken` (called by dealDamage on hostile damage). */
export function transferDamage(ctx: SimContext, s: Unit, taken: number, req: DamageRequest, depth: number): void {
  if (taken <= 0 || req.selfHarm) return;
  for (const inst of s.auras) {
    const tr = ctx.content.aura(inst.auraId)?.transfer;
    if (!tr) continue;
    if (tr.schools && !tr.schools.includes(req.school)) continue;
    const marked: Unit[] = [];
    const r = tr.radius ?? 40;
    for (const id of ctx.world.unitOrder) {
      const u = unitOf(ctx, id);
      if (!u?.alive || u.faction !== s.faction || u.pet) continue;
      if (!u.auras.some((a) => a.auraId === tr.markAuraId && a.casterId === s.id)) continue;
      if (Math.hypot(u.pos.x - s.pos.x, u.pos.y - s.pos.y) > r) continue;
      marked.push(u);
    }
    if (marked.length === 0) continue;
    const share = tr.shareAbove !== undefined && marked.length > tr.shareAbove ? tr.shareAbove / marked.length : 1;
    const amount = taken * tr.pct * share;
    for (const u of marked) heal(ctx, { sourceId: s.id, targetId: u.id, abilityId: tr.markAuraId, amount, preScaled: true, canCrit: false }, { depth: depth + 1 });
  }
}
